// ICD-10 problem blot (Task #38).
//
// Renders an ICD-10 problem line in the A&P section as a bold, interactive
// block. Click the pencil → enters edit mode: an inline search box + drop-down
// of ICD-10 matches. Pick one → blot re-renders with the new problem name +
// (optional) the code as a small chip. Cancels with Esc or × button.
//
// Why BlockEmbed:
//   - Each numbered problem is conceptually its own atomic line in A&P.
//   - Same Yjs / y-quill round-trip story as VitalsGridBlot / RosGridBlot /
//     PendingEditBlot — all proven through this stack.
//   - Edit mode can pop a dropdown without disturbing surrounding text.
//
// Data shape (stored as data-icd-problem JSON on the node):
//   {
//     id: 'icd_1',                                  // stable id within doc
//     problemNumber: 1,                             // "1." / "2." / ...
//     displayName: 'Obstructive sleep apnea, ...',  // what the clinician sees
//     icd10Code: 'G47.30',                          // optional; null until set
//     source: { label: 'fill-template', section: 'ASSESSMENT & PLAN' },
//   }
//
// Future hooks (not in v1):
//   - Sync icd10Code back to a structured codes column on the note row so
//     downstream billing/extraction can read it without re-parsing the text.
//   - Specialty-aware autocomplete ranking (cardio terms first for cardio
//     visits, etc.) via a context-passing prop.
//   - Live API search (clinicaltables.nlm.nih.gov ICD-10-CM) when the static
//     list misses — currently degrades to "no suggestions, type your own".

import Quill from 'quill';
import { searchIcd10, getProblemByCode, inferCodeFromName } from './icd10CommonProblems';
import { getBackendURL } from '../../../components/env';

// Task #190 Phase 1: cross-render request tracker so a stale backend
// response (slower) doesn't overwrite a newer query's results.
let _icd10LiveSeq = 0;
const _icd10LiveCache = new Map(); // q -> results (small in-memory dedup)

async function fetchLiveIcd10(query, limit = 8) {
  const q = String(query || '').trim();
  if (!q) return [];
  if (_icd10LiveCache.has(q)) return _icd10LiveCache.get(q);
  try {
    const url = `${getBackendURL()}/api/icd10/search/?q=${encodeURIComponent(q)}&limit=${limit}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    const rows = Array.isArray(data?.results) ? data.results : [];
    const mapped = rows.map((r) => ({
      code: r.code,
      name: r.short_desc || r.long_desc || r.code,
      aliases: [],
      _source: 'live',
    }));
    _icd10LiveCache.set(q, mapped);
    return mapped;
  } catch (_) {
    return [];
  }
}

const BlockEmbed = Quill.import('blots/block/embed');

export const ICD_PROBLEM_BLOT_NAME = 'icd-problem';
const TAG_NAME = 'div';
const CLASS_NAME = 'ql-icd-problem';

// Track which blot is currently in edit mode so we can close the prior one
// when another opens. (Quill instances live longer than any single blot, so
// we key this in module scope.)
let activeEditNodeId = null;

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

// Display mode: bold problem line with a pencil-edit affordance and an
// optional code chip. Matches the visual weight of `**N. Problem**` so the
// note doesn't lurch when the blot replaces plain bold text.
function buildDisplayHtml(value) {
  // problemNumber 0/null = unnumbered problem, renders '#' (Leejoe 2026-07-07:
  // unnumbered problems reorder freely until drag-reorder ships). Legacy chips
  // with real numbers keep rendering 'N.'.
  const num = value && value.problemNumber ? String(value.problemNumber) : '';
  const name = String(value && value.displayName || '');
  const code = String(value && value.icd10Code || '');
  const numHtml = num
    ? `<span class="icd-num font-bold">${escapeHtml(num)}.</span> `
    : '<span class="icd-num font-bold">#</span> ';
  // Task #268 (recreated 2026-07-07 — the original fix was lost in a merge):
  // `select-none` + user-select:none on UI chrome so browser copy (⌘C →
  // NextGen paste) carries only the problem text, never the pencil button
  // or code-chip styling Noreen had to strip via Google Docs.
  const codeHtml = code
    ? `<span class="icd-code select-none ml-2 inline-flex items-center text-[10px] font-mono uppercase tracking-wide bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-200 px-1.5 py-0.5 rounded" title="ICD-10" style="user-select:none;-webkit-user-select:none">${escapeHtml(code)}</span>`
    : '<span class="icd-code icd-code-missing select-none ml-2 inline-flex items-center text-[10px] font-mono uppercase tracking-wide bg-slate-100 dark:bg-slate-700/40 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded border border-dashed border-slate-300 dark:border-slate-600" title="No ICD-10 code set — click pencil to pick" style="user-select:none;-webkit-user-select:none">+ ICD-10</span>';
  return [
    '<div class="icd-problem-display flex items-center gap-2 py-0.5">',
    '  <div class="icd-problem-text flex-1 min-w-0 font-bold">',
    `    ${numHtml}${escapeHtml(name)}`,
    `    ${codeHtml}`,
    '  </div>',
    '  <div class="icd-problem-controls select-none flex shrink-0 items-center gap-1" contenteditable="false" style="user-select:none;-webkit-user-select:none">',
    '    <button type="button" class="icd-problem-btn icd-problem-edit text-xs px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600" title="Search ICD-10" aria-label="Search ICD-10">&#9998;</button>',
    '  </div>',
    '</div>',
  ].join('');
}

// Edit mode: search input + dropdown. We render the dropdown EMPTY at create
// time — the click handler populates it via searchIcd10() once the user types.
// Shows the current selection above so the clinician knows what they're
// editing.
function buildEditHtml(value) {
  const name = String(value && value.displayName || '');
  const code = String(value && value.icd10Code || '');
  const currentChip = code ? ` <span class="icd-code font-mono text-[10px] uppercase tracking-wide bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded">${escapeHtml(code)}</span>` : '';
  return [
    '<div class="icd-problem-edit-shell border border-sky-300 dark:border-sky-500/40 rounded-md bg-sky-50/40 dark:bg-sky-500/[0.06] p-2" contenteditable="false">',
    '  <div class="icd-problem-edit-header flex items-center justify-between gap-2 mb-1.5">',
    `    <span class="icd-problem-edit-current text-xs text-slate-600 dark:text-slate-300">Editing: <em>${escapeHtml(name)}</em>${currentChip}</span>`,
    '    <button type="button" class="icd-problem-btn icd-problem-cancel text-xs px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600" title="Cancel (Esc)" aria-label="Cancel">&times;</button>',
    '  </div>',
    `  <input type="text" class="icd-problem-search w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Search ICD-10 — e.g., diabetes, headache, GERD…" value="${escapeHtml(name)}" autocomplete="off" />`,
    '  <ul class="icd-problem-suggestions mt-1 max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-sm" role="listbox"></ul>',
    '  <div class="icd-problem-edit-hint mt-1 text-[10px] text-slate-500 dark:text-slate-400">↑↓ navigate · Enter pick · Esc cancel</div>',
    '</div>',
  ].join('');
}

class IcdProblemBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    const data = value || {};
    if (!data.id) data.id = `icd_${Math.random().toString(36).slice(2, 9)}`;
    // Back-fill the code from the name if the AI gave us only text. Safe
    // because inferCodeFromName only returns high-confidence matches.
    if (!data.icd10Code && data.displayName) {
      const guess = inferCodeFromName(data.displayName);
      if (guess) data.icd10Code = guess;
    }
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-icd-problem', JSON.stringify(data));
    node.setAttribute('data-icd-id', String(data.id));
    node.innerHTML = buildDisplayHtml(data);
    return node;
  }

  static value(node) {
    const raw = node.getAttribute('data-icd-problem');
    if (!raw) return {};
    try { return JSON.parse(raw); } catch (_) { return {}; }
  }
}

IcdProblemBlot.blotName = ICD_PROBLEM_BLOT_NAME;
IcdProblemBlot.tagName = TAG_NAME;
IcdProblemBlot.className = CLASS_NAME;

let registered = false;
export function registerIcdProblemBlot() {
  if (registered) return;
  Quill.register(IcdProblemBlot);
  registered = true;
}

// Task #229 — update an existing blot's stored value (displayName +
// icd10Code) and re-render the display HTML. Used by AddProblemDialog's
// edit-mode commit path so the pencil-click flow doesn't have to insert
// a new blot. Looks up the node by icd-id across the whole document so
// the caller doesn't have to thread editor refs through React state.
export function updateIcdProblemNodeValue(icdId, { displayName, icd10Code }) {
  if (!icdId) return false;
  const node = document.querySelector(`.${CLASS_NAME}[data-icd-id="${String(icdId).replace(/"/g, '\\"')}"]`);
  if (!node) return false;
  let saved = {};
  try {
    const raw = node.getAttribute('data-icd-problem');
    if (raw) saved = JSON.parse(raw);
  } catch (_) { /* fall through with empty saved */ }
  const next = {
    ...saved,
    id: saved.id || icdId,
    displayName: String(displayName || '').trim(),
    icd10Code: String(icd10Code || '').trim(),
  };
  node.setAttribute('data-icd-problem', JSON.stringify(next));
  node.innerHTML = buildDisplayHtml(next);
  return true;
}

// Find a blot's DOM node by its data-icd-id.
export function findIcdProblemNode(quill, icdId) {
  if (!quill || !quill.root) return null;
  return quill.root.querySelector(`.${CLASS_NAME}[data-icd-id="${String(icdId).replace(/"/g, '\\"')}"]`);
}

// Task #223 — insert a FULLY-FORMED blot (caller already has displayName +
// icd10Code from AddProblemDialog or similar). Skips the inline-edit-mode
// that has the recurring 1-letter typing bug. The dialog ate the keystrokes;
// we just stamp the result.

// Find the Quill index of the END of the A&P section — right before the next
// section header after A&P (or end-of-doc when A&P is the last section). New
// problems get their OWN area after the last problem's full content; the old
// "right after the last chip" rule dropped them inside the PREVIOUS problem's
// body (Leejoe 2026-07-06: added CHF landed between Hyperlipidemia's header
// and its plan bullets, reading as its plan). Returns the insertEmbed index.
function findAssessmentInsertIndex(quill) {
  if (!quill || !quill.root) return Math.max(0, (quill?.getLength?.() ?? 1) - 1);
  const headings = quill.root.querySelectorAll('h1, h2, h3, h4');
  // Walk DOM order; A&P heading is anything containing "ASSESSMENT" or
  // "Assessment" + "Plan" (or "A&P"). Find it, then locate the next heading
  // after it — insertion point sits between them.
  const isApHeading = (el) => {
    const t = (el.textContent || '').trim().toUpperCase();
    return /ASSESSMENT\s*(&|AND|\/)\s*PLAN|^A\s*&\s*P$|^IMPRESSION$/i.test(t);
  };
  let apHeading = null;
  for (const h of headings) {
    if (isApHeading(h)) { apHeading = h; break; }
  }
  if (!apHeading) {
    // Notes whose A&P header is a PLAIN paragraph — fresh template fills and
    // notes accepted before Master Note emitted real headings — have no
    // h1-h4 to find. Scan top-level blocks for a line that IS the header
    // (exact-ish match so prose mentioning "assessment and plan" is skipped).
    const AP_EXACT = /^(ASSESSMENT\s*(&|AND|\/)\s*PLAN|A\s*&\s*P|IMPRESSION)\s*:?\s*$/i;
    for (const b of quill.root.children) {
      const t = (b.textContent || '').trim();
      if (t.length <= 40 && AP_EXACT.test(t)) { apHeading = b; break; }
    }
  }
  if (!apHeading) return Math.max(0, quill.getLength() - 1);
  // Walk forward from the A&P heading to the END of the section — the next
  // section header after it. A header is a real h1-h6 OR a standalone
  // ALL-CAPS line ("SCORES / RATING SCALES", "ORDERS") so plain-text notes
  // bound correctly too. Same caps rule as the Master Note Accept transform.
  const SHORT_SECTIONS = new Set(['HPI', 'ROS', 'PE', 'PMH', 'PSH', 'FH', 'SH', 'CC', 'PLAN', 'LABS', 'MEDS', 'EXAM']);
  const isPlainSectionLine = (el) => {
    const bare = (el.textContent || '').trim().replace(/:\s*$/, '').trim();
    if (!bare || bare.length > 40 || !/^[A-Z][A-Z &/()\-]*$/.test(bare)) return false;
    return bare.length >= 5 || SHORT_SECTIONS.has(bare.replace(/\s+/g, ''));
  };
  let sectionEnd = null;
  let cur = apHeading.nextElementSibling;
  while (cur) {
    const tag = (cur.tagName || '').toUpperCase();
    if ((/^H[1-6]$/.test(tag) || isPlainSectionLine(cur)) && !isApHeading(cur)) {
      sectionEnd = cur;
      break;
    }
    cur = cur.nextElementSibling;
  }
  try {
    const Quill = quill.constructor;
    if (sectionEnd) {
      const blot = Quill.find(sectionEnd);
      if (blot) return Math.max(0, quill.getIndex(blot));
    }
  } catch (_) { /* fall through */ }
  // No boundary → A&P is the last section; end-of-document IS end-of-section.
  return Math.max(0, quill.getLength() - 1);
}

export function insertIcdProblemFormed(quill, { displayName = '', icd10Code = '' } = {}) {
  if (!quill || !quill.root) return null;
  const enabled = typeof quill.isEnabled === 'function'
    ? quill.isEnabled()
    : quill.root.getAttribute('contenteditable') !== 'false';
  if (!enabled) {
    console.warn('[icdProblemBlot] editor disabled (read mode) — insert skipped');
    return null;
  }
  const newId = `icd_${Math.random().toString(36).slice(2, 9)}`;
  const value = {
    id: newId,
    problemNumber: 0, // unnumbered — chip renders '#'
    displayName: String(displayName || '').trim(),
    icd10Code: String(icd10Code || '').trim(),
  };
  // Task #229 — insert into A&P section (last problem there) instead of
  // end-of-document. End-of-doc landed problems AFTER the time-spent footer.
  const insertIndex = findAssessmentInsertIndex(quill);
  try {
    quill.insertEmbed(insertIndex, ICD_PROBLEM_BLOT_NAME, value, 'user');
    // Give the new problem its own writing area: an empty line right under
    // the chip, caret placed there so the plan can be typed immediately.
    quill.insertText(insertIndex + 1, '\n', 'user');
    quill.setSelection(insertIndex + 1, 0, 'user');
    setTimeout(() => {
      const node = findIcdProblemNode(quill, newId);
      if (node) {
        try { node.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
      }
    }, 0);
    return newId;
  } catch (e) {
    console.warn('[icdProblemBlot] insertIcdProblemFormed failed:', e);
    return null;
  }
}

// Task #170 — Add new ICD-10 problem button. Inserts a new icd-problem blot
// at the end of the document with the next available problemNumber, then
// opens its edit-mode inline search so the clinician can type the diagnosis
// immediately. Called from the document toolbar's "+ ICD" button.
//
// SUPERSEDED 2026-06-28 by insertIcdProblemFormed + AddProblemDialog
// (Task #223). Keeping this function for any legacy callers that haven't
// migrated; new code should use the dialog flow.
export function addIcdProblemAtEnd(quill) {
  if (!quill || !quill.root) return null;
  // Count existing problems to compute the next problemNumber.
  const existing = quill.root.querySelectorAll(`.${CLASS_NAME}`);
  const nextNumber = existing.length + 1;
  const newId = `icd_${Math.random().toString(36).slice(2, 9)}`;
  const value = {
    id: newId,
    problemNumber: nextNumber,
    displayName: '',
    icd10Code: '',
  };
  // Insert at end of document. insertEmbed appends the blot + a newline,
  // which keeps the cursor on a fresh line after the new problem so the
  // next typed character starts cleanly below it.
  const insertIndex = quill.getLength() - 1; // -1 because Quill always has a trailing \n
  try {
    quill.insertEmbed(insertIndex, ICD_PROBLEM_BLOT_NAME, value, 'user');
    // Scroll the new blot into view so the user knows where it landed.
    setTimeout(() => {
      const node = findIcdProblemNode(quill, newId);
      if (node) {
        try { node.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* no-op */ }
        // Open edit mode so the search input is focused and ready for typing.
        enterEditMode(quill, node);
      }
    }, 0);
  } catch (e) {
    console.warn('[icdProblemBlot] addIcdProblemAtEnd failed:', e);
    return null;
  }
  return newId;
}

function readValue(node) {
  return IcdProblemBlot.value(node);
}

// Module-level in-flight typed-value cache, keyed by blot id. Survives blot
// re-renders triggered by Quill's mutation observer mid-keystroke, so the
// user's typed value isn't reset to the last committed displayName when
// refreshSuggestions mutates the <ul> inside our blot.
//
// Without this, the "1 letter at a time" bug fires: user types 'a', the
// suggestions <ul> rewrite triggers a re-render of the blot's innerHTML,
// the input's value attribute resets to the (still empty) displayName,
// and the user has to start over on every keystroke.
//
// Lifecycle:
//   - input handler stashes the current value every keystroke
//   - renderEdit reads from here first, falls back to committed displayName
//   - commitSelection + cancelEdit clear the entry so the next edit starts
//     from the canonical committed state
const _inFlightTypedValue = new Map();

function writeValue(node, nextValue) {
  node.setAttribute('data-icd-problem', JSON.stringify(nextValue || {}));
}

function renderDisplay(node) {
  const value = readValue(node);
  node.innerHTML = buildDisplayHtml(value);
}

function renderEdit(quill, node) {
  const value = readValue(node);
  // Preserve typed value across mid-keystroke re-renders. If the user is
  // actively typing (in-flight value present), use that instead of the
  // committed displayName so the input doesn't reset to empty/old text.
  const blotId = node.getAttribute('data-icd-id');
  const inFlight = blotId ? _inFlightTypedValue.get(blotId) : undefined;
  const renderValue = inFlight != null
    ? { ...value, displayName: inFlight }
    : value;
  node.innerHTML = buildEditHtml(renderValue);
  const input = node.querySelector('.icd-problem-search');
  if (input) {
    // Two layers of fixes for the focus-stealing bug:
    //
    // (1) Stop input + keydown events at the input so they never reach Quill's
    //     root listeners. Without this, Quill's text-change / keyboard module
    //     would treat keystrokes as editor input.
    // (2) Quill ALSO subscribes to document `selectionchange`. When the
    //     browser cursor moves inside our input (every keystroke moves the
    //     caret), the global event fires, Quill normalizes by reasserting its
    //     editor caret position, and focus leaves our input. Calling
    //     quill.blur() / quill.setSelection(null) when we enter edit mode
    //     releases Quill's selection tracking so it stops reacting to the
    //     cursor movements happening inside our embedded input.
    // (3) Belt-and-suspenders: after handling each input event, if focus has
    //     somehow left the input anyway, refocus it and restore the caret to
    //     where the user was typing. Cheap and bulletproof.
    input.addEventListener('input', (e) => {
      e.stopPropagation();
      const caret = input.selectionStart;
      // Stash the current typed value so any Quill-driven re-render of the
      // blot picks it up instead of resetting to the empty committed name.
      // Fixes the "only 1 letter at a time" bug.
      const blotId = node.getAttribute('data-icd-id');
      if (blotId) _inFlightTypedValue.set(blotId, input.value);
      refreshSuggestions(node, input.value);
      if (document.activeElement !== input) {
        try { input.focus(); input.setSelectionRange(caret, caret); } catch (_) {}
      }
    });
    input.addEventListener('keydown', (e) => {
      // Stop the keydown from reaching Quill's keyboard module. Without this,
      // Backspace would delete the blot, arrows would move the editor caret,
      // Enter would insert a new line, etc.
      e.stopPropagation();
      const ul = node.querySelector('.icd-problem-suggestions');
      if (e.key === 'Escape') {
        cancelEdit(quill, node);
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter') {
        const active = ul && ul.querySelector('.icd-problem-suggestion.is-active');
        if (active && !active.classList.contains('icd-problem-suggestion-empty')) {
          const code = active.getAttribute('data-icd-code') || '';
          const name = active.getAttribute('data-icd-name') || '';
          commitSelection(quill, node, name, code);
        } else {
          commitSelection(quill, node, input.value, '');
        }
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!ul) return;
        const items = Array.from(ul.querySelectorAll('.icd-problem-suggestion:not(.icd-problem-suggestion-empty)'));
        if (!items.length) return;
        const currentIdx = items.findIndex((el) => el.classList.contains('is-active'));
        let nextIdx = currentIdx;
        if (e.key === 'ArrowDown') nextIdx = (currentIdx + 1) % items.length;
        else nextIdx = (currentIdx - 1 + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-active', i === nextIdx));
        e.preventDefault();
      }
    });
    // Defer focus to next tick so Quill doesn't steal it back when the blot
    // re-renders inside its own event delegate.
    setTimeout(() => {
      try { input.focus(); input.select(); } catch (_) {}
      refreshSuggestions(node, input.value);
    }, 0);
  }
}

// Render suggestion rows. Static results show 'common' badge, live (backend
// catalog) results show 'catalog' badge — helps the clinician trust common
// problems are first-class while still seeing the long tail.
function _renderSuggestions(ul, matches) {
  if (!matches.length) {
    ul.innerHTML = '<li class="icd-problem-suggestion-empty px-2 py-1 text-xs italic text-slate-500 dark:text-slate-400">No matches — type your own and press Enter to keep as-is.</li>';
    return;
  }
  ul.innerHTML = matches.map((m, idx) => {
    const activeCls = idx === 0
      ? ' bg-sky-100 dark:bg-sky-500/20 is-active'
      : '';
    return (
      `<li class="icd-problem-suggestion${activeCls} flex items-center justify-between gap-2 px-2 py-1 cursor-pointer hover:bg-sky-50 dark:hover:bg-slate-700/50" role="option" data-icd-code="${escapeHtml(m.code)}" data-icd-name="${escapeHtml(m.name)}">` +
      `<span class="icd-suggestion-name truncate">${escapeHtml(m.name)}</span>` +
      `<span class="icd-suggestion-code font-mono text-[10px] uppercase tracking-wide bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-200 px-1.5 py-0.5 rounded shrink-0">${escapeHtml(m.code)}</span>` +
      '</li>'
    );
  }).join('');
}

// Dedup by code, preferring whichever entry comes first (static beats live
// since static carries curated names + aliases).
function _mergeIcd10Results(staticHits, liveHits, limit) {
  const seen = new Set();
  const out = [];
  for (const m of staticHits) {
    if (m && m.code && !seen.has(m.code)) {
      seen.add(m.code);
      out.push(m);
      if (out.length >= limit) return out;
    }
  }
  for (const m of liveHits) {
    if (m && m.code && !seen.has(m.code)) {
      seen.add(m.code);
      out.push(m);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

// Update the suggestions <ul> inside an edit-mode blot.
//
// Task #190 Phase 1 swap: render the static 188-entry list IMMEDIATELY for
// snappy feedback, then asynchronously merge in backend catalog hits (97K
// rows) so the long tail is reachable. The static list is curated with
// aliases + clinical names; backend gives raw CMS short_desc. Both stay.
function refreshSuggestions(node, query) {
  const ul = node.querySelector('.icd-problem-suggestions');
  if (!ul) return;
  const limit = 8;
  const staticMatches = searchIcd10(query || '', limit);
  // Instant paint with whatever static gives us.
  _renderSuggestions(ul, staticMatches);
  const q = String(query || '').trim();
  if (!q) return;
  // Don't ping backend for tiny queries — static covers single-char prefixes.
  if (q.length < 2) return;
  const reqId = ++_icd10LiveSeq;
  fetchLiveIcd10(q, limit).then((liveHits) => {
    // Skip if a newer query has already fired.
    if (reqId !== _icd10LiveSeq) return;
    // Skip if the blot has navigated away from edit mode.
    if (!document.body.contains(ul)) return;
    const merged = _mergeIcd10Results(staticMatches, liveHits, limit);
    if (merged.length === staticMatches.length) return; // no new hits, don't re-render
    _renderSuggestions(ul, merged);
  });
}

function _quillSetEnabled(quill, enabled) {
  // Heavy hammer: enable(false) puts Quill in read-only mode, which suspends
  // its keyboard module, format observers, selection tracking, and text-change
  // listeners. Previous fixes (stopPropagation on input/keydown, quill.blur()
  // on enter) weren't enough — Quill's document-level selectionchange handler
  // STILL re-engaged on every keystroke because the user's cursor is inside
  // our <input>, which is a descendant of contenteditable=false embed, which
  // is still inside the editor's DOM tree. enable(false) tells Quill to stop
  // observing entirely. Re-enable on commit / cancel.
  if (!quill) return;
  try { if (typeof quill.enable === 'function') quill.enable(!!enabled); } catch (_) {}
}

function commitSelection(quill, node, problemNameOverride, codeOverride) {
  const value = readValue(node);
  const nextName = problemNameOverride != null ? problemNameOverride : value.displayName;
  const nextCode = codeOverride != null ? codeOverride : value.icd10Code;
  const next = { ...value, displayName: String(nextName || ''), icd10Code: String(nextCode || '') };
  writeValue(node, next);
  renderDisplay(node);
  activeEditNodeId = null;
  // Clear in-flight typed value so the NEXT edit starts from the canonical
  // committed displayName.
  const blotId = node.getAttribute('data-icd-id');
  if (blotId) _inFlightTypedValue.delete(blotId);
  _quillSetEnabled(quill, true);
}

function cancelEdit(quill, node) {
  // Task #223 followup (Leejoe 2026-06-28): if the blot was a NEWLY added
  // empty problem (no displayName, no icd10Code) and the clinician cancels
  // before picking anything, REMOVE the blot entirely instead of leaving a
  // dangling "1." with blank content. Only applies to empty problems —
  // editing an existing problem and cancelling preserves the prior value
  // (existing render-display path below).
  const raw = node.getAttribute('data-icd-problem');
  let saved = {};
  try { saved = raw ? JSON.parse(raw) : {}; } catch (_) {}
  const isEmptyNewProblem = !((saved.displayName || '').trim() || (saved.icd10Code || '').trim());
  activeEditNodeId = null;
  const blotId = node.getAttribute('data-icd-id');
  if (blotId) _inFlightTypedValue.delete(blotId);
  _quillSetEnabled(quill, true);
  if (isEmptyNewProblem && quill && typeof quill.getIndex === 'function') {
    try {
      const Quill = quill.constructor;
      const blot = Quill.find(node);
      if (blot) {
        const index = quill.getIndex(blot);
        // BlockEmbed occupies length 1 — remove the blot itself.
        quill.deleteText(index, 1, 'user');
        return;
      }
    } catch (_) { /* fall through to render display */ }
  }
  renderDisplay(node);
}

function enterEditMode(quill, node) {
  // Close any other blot currently editing (single-edit invariant).
  if (activeEditNodeId && activeEditNodeId !== node.getAttribute('data-icd-id')) {
    const prior = document.querySelector(`.${CLASS_NAME}[data-icd-id="${activeEditNodeId}"]`);
    if (prior) renderDisplay(prior);
  }
  activeEditNodeId = node.getAttribute('data-icd-id');
  renderEdit(quill, node);
  _quillSetEnabled(quill, false);
}

// Delegated event handler — host editor calls attachIcdProblemControls(quill)
// once on editor mount. We listen on the editor root and decide per-event
// which blot was clicked / typed in.
export function attachIcdProblemControls(quill) {
  if (!quill || !quill.root) return;
  const root = quill.root;
  if (root.__icdProblemAttached) return;
  root.__icdProblemAttached = true;

  // Click handler: pencil opens edit; cancel closes; suggestion picks commit.
  root.addEventListener('click', (e) => {
    const blotNode = e.target.closest(`.${CLASS_NAME}`);
    if (!blotNode) return;
    if (e.target.closest('.icd-problem-edit')) {
      // Task #229 (Leejoe 2026-06-29): route pencil clicks to the React
      // AddProblemDialog in edit mode instead of the inline Quill
      // edit-mode UI. The inline path has had a recurring 1-letter-at-
      // a-time typing bug (same Quill-vs-contenteditable-input war that
      // killed new-problem create in Task #223). Dispatching a window
      // event keeps this blot module free of React imports; PatientHeader
      // Bar listens and opens AddProblemDialog with the existing values
      // pre-filled. On commit the dialog calls updateIcdProblemNodeValue
      // (also exported here) instead of inserting a new blot.
      try {
        const raw = blotNode.getAttribute('data-icd-problem');
        const saved = raw ? JSON.parse(raw) : {};
        const detail = {
          id: blotNode.getAttribute('data-icd-id') || saved.id || '',
          displayName: saved.displayName || '',
          icd10Code: saved.icd10Code || '',
        };
        window.dispatchEvent(new CustomEvent('vmai:edit-icd-problem', { detail }));
      } catch (_) {
        // Parsing failure — fall through to inline editor as a safety net.
        enterEditMode(quill, blotNode);
      }
      e.preventDefault();
      return;
    }
    if (e.target.closest('.icd-problem-cancel')) {
      cancelEdit(quill, blotNode);
      e.preventDefault();
      return;
    }
    const sugg = e.target.closest('.icd-problem-suggestion');
    if (sugg && !sugg.classList.contains('icd-problem-suggestion-empty')) {
      const code = sugg.getAttribute('data-icd-code') || '';
      const name = sugg.getAttribute('data-icd-name') || '';
      commitSelection(quill, blotNode, name, code);
      e.preventDefault();
      return;
    }
  });

  // NOTE: input + keydown for the search field are NOT delegated on root
  // anymore — they're attached directly to the input element in renderEdit so
  // we can stopPropagation() before Quill's listeners see the events. (Quill
  // was reasserting the editor caret on every keystroke, yanking focus out of
  // our search box after each character.) Click delegation stays here because
  // suggestion clicks are dynamic (refreshSuggestions rewrites the ul) and
  // Quill doesn't fight us on click events to contenteditable=false content.
}

// Insert an ICD problem blot at the given Quill index. value should match the
// data shape documented at the top of this file.
export function insertIcdProblem(quill, index, value) {
  if (!quill) return -1;
  registerIcdProblemBlot();
  const safeIndex = Math.max(0, Math.min(typeof index === 'number' ? index : quill.getLength(), quill.getLength()));
  quill.insertEmbed(safeIndex, ICD_PROBLEM_BLOT_NAME, value || {}, 'user');
  return safeIndex;
}

// Convenience: replace a stored display name with the canonical name from
// the registry (used when re-rendering an existing blot if the AI emitted a
// slightly different phrasing than what we have in the static list).
export function reconcileDisplayName(blotValue) {
  if (!blotValue || !blotValue.icd10Code) return blotValue;
  const canonical = getProblemByCode(blotValue.icd10Code);
  if (!canonical) return blotValue;
  return { ...blotValue, displayName: canonical.name };
}

export default IcdProblemBlot;
